import { useState } from 'react';
import { CheckCircle, Clock, XCircle, Send, Download, AlertCircle } from 'lucide-react';
import { authenticatedFetch } from '../../lib/auth';

export type LmsType = 'moodle' | 'tutorlms' | 'learndash' | 'xtramile';

export interface ApprovalIntegration {
  id: string;
  connection_tested?: boolean;
  agreement_confirmed?: boolean;
  quality_control_confirmed?: boolean;
  content_alignment_confirmed?: boolean;
  assessment_guidelines_confirmed?: boolean;
  approved_by_forbund?: boolean;
  approved_by_forbund_at?: string | null;
  approval_requested_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  agreement_pdf_path?: string | null;
  agreement_pdf_generated_at?: string | null;
}

interface Props {
  lmsType: LmsType;
  memberId: string;
  integration: ApprovalIntegration;
  onRefresh: () => void;
}

const LMS_DISPLAY: Record<LmsType, string> = {
  moodle: 'Moodle',
  tutorlms: 'TutorLMS',
  learndash: 'LearnDash',
  xtramile: 'Xtramile'
};

export default function IntegrationApprovalPanel({ lmsType, memberId, integration, onRefresh }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const display = LMS_DISPLAY[lmsType];

  const allAgreementsSigned =
    integration.agreement_confirmed &&
    integration.quality_control_confirmed &&
    integration.content_alignment_confirmed &&
    integration.assessment_guidelines_confirmed;

  const canSubmit =
    !!integration.connection_tested &&
    !!allAgreementsSigned &&
    !integration.approved_by_forbund &&
    !integration.approval_requested_at;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `/api/${lmsType}/integrations/member/${memberId}/submit-for-approval`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunne ikke sende forespørsel');
      }
      onRefresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Approved state
  if (integration.approved_by_forbund) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-medium text-green-800">{display}-integrasjon aktiv</h3>
            <p className="text-sm text-green-700 mt-1">
              Godkjent av studieforbundet. Fullføringer synkroniseres automatisk.
            </p>
            {integration.approved_by_forbund_at && (
              <p className="text-xs text-green-600 mt-1">
                Godkjent: {new Date(integration.approved_by_forbund_at).toLocaleString('nb-NO')}
              </p>
            )}
            {integration.agreement_pdf_path && (
              <a
                href={`/api/${lmsType}/integrations/${integration.id}/agreement-pdf`}
                download
                className="inline-flex items-center gap-1 mt-2 text-sm text-green-700 hover:text-green-900 underline"
              >
                <Download className="h-4 w-4" />
                Last ned signert avtale (PDF)
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Rejected state
  if (integration.rejected_at) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-medium text-red-800">Forespørsel avslått</h3>
            {integration.rejection_reason && (
              <div className="mt-2 p-3 bg-white border border-red-200 rounded">
                <p className="text-xs uppercase tracking-wide text-red-700 font-semibold mb-1">Begrunnelse</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{integration.rejection_reason}</p>
              </div>
            )}
            <p className="text-sm text-red-700 mt-2">
              Du kan rette opp forholdene og sende inn ny forespørsel.
            </p>
            {canSubmit && (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Sender...' : 'Send inn ny forespørsel'}
              </button>
            )}
          </div>
        </div>
        {error && <p className="mt-2 text-sm text-red-600 pl-8">{error}</p>}
      </div>
    );
  }

  // Pending state
  if (integration.approval_requested_at) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-800">Venter på studieforbundet</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Forespørselen er sendt. Studieforbundet mottar e-postvarsel og varsel i sitt kontrollpanel.
              Du får beskjed når den er behandlet.
            </p>
            <p className="text-xs text-yellow-700 mt-2">
              Sendt: {new Date(integration.approval_requested_at).toLocaleString('nb-NO')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Draft / not-yet-submitted state
  const missingSteps: string[] = [];
  if (!integration.connection_tested) missingSteps.push('Test tilkoblingen');
  if (!allAgreementsSigned) missingSteps.push('Bekreft alle avtaler');

  return (
    <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-kf-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-medium text-kf-blue-800">Send inn forespørsel til studieforbundet</h3>
          <p className="text-sm text-kf-blue-700 mt-1">
            Når forespørselen er godkjent, genereres en signert PDF-avtale som sendes til deg,
            studieforbundet og Kompetanseforbundet.
          </p>

          {missingSteps.length > 0 && (
            <div className="mt-3 p-3 bg-white border border-kf-blue-200 rounded">
              <p className="text-xs font-medium text-kf-blue-800 mb-1">Gjenstår før du kan sende:</p>
              <ul className="text-sm text-kf-blue-700 list-disc list-inside">
                {missingSteps.map(s => <li key={s}>{s}</li>)}
              </ul>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            {submitting ? 'Sender...' : 'Send inn forespørsel'}
          </button>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
